import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StorefrontIcon, TagIcon } from "@phosphor-icons/react";

import { ChipSelect } from "@shared/components";
import type { ChipSelectOption } from "@shared/components";
import { useGetRatePlansSellersList } from "@payfac/services";
import { getMccLabel, MCC_DEFAULT_KEY } from "@payfac/constants";

import styles from "./rate-plan-params.module.css";

import type { RatePlanParamsProps } from "./rate-plan-params.type";

export const RatePlanParams = ({
    planId,
    availableMccs,
    registeredMcc,
}: RatePlanParamsProps) => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const sellerId = searchParams.get("sellerId") ?? "";
    const mccParam = searchParams.get("mcc") ?? MCC_DEFAULT_KEY;

    const { data: sellersResponse, isLoading: isLoadingSellers } =
        useGetRatePlansSellersList({ planId, filters: { page: 1, limit: 200 } });

    const sellerOptions = useMemo<ChipSelectOption[]>(
        () =>
            (sellersResponse?.data?.data ?? []).map((seller) => ({
                value: String(seller.id),
                label: seller.companyName,
                description: seller.document,
            })),
        [sellersResponse],
    );

    const updateParam = (key: string, value: string | null) => {
        const nextParams = new URLSearchParams(searchParams);
        if (value === null) {
            nextParams.delete(key);
        } else {
            nextParams.set(key, value);
        }
        setSearchParams(nextParams);
    };

    const mccLocked = !!sellerId;

    const mccValue = mccLocked ? registeredMcc || MCC_DEFAULT_KEY : mccParam;

    const mccOptions = useMemo<ChipSelectOption[]>(() => {
        const codes = mccLocked ? [mccValue] : availableMccs;
        return codes.map((code) => ({ value: code, label: getMccLabel(code) }));
    }, [mccLocked, mccValue, availableMccs]);

    const showSeller = sellerOptions.length > 0;
    const showMcc = mccLocked || availableMccs.length > 0;

    if (!showSeller && !showMcc) return null;

    return (
        <section className={styles.params}>
            <div className={styles.header}>
                <span className={styles.title}>
                    {t("payfac.pageRatePlanParamsTitle")}
                </span>
                <span className={styles.divider} />
            </div>

            <div className={styles.chips}>
                {showSeller && (
                    <ChipSelect
                        label={t("payfac.pageRatePlanParamsSeller")}
                        value={sellerId || null}
                        options={sellerOptions}
                        onChange={(value) => updateParam("sellerId", value)}
                        onClear={() => updateParam("sellerId", null)}
                        clearable
                        disabled={isLoadingSellers}
                        icon={<StorefrontIcon />}
                        placeholder={t("payfac.pageRatePlanParamsSellerPlaceholder")}
                        searchPlaceholder={t("payfac.pageRatePlanParamsSellerSearch")}
                        emptyText={t("payfac.pageRatePlanParamsSellerEmpty")}
                    />
                )}

                {showMcc && (
                    <ChipSelect
                        label={
                            mccLocked
                                ? t("payfac.pageRatePlanParamsMccLocked")
                                : t("payfac.pageRatePlanParamsMcc")
                        }
                        value={mccValue}
                        options={mccOptions}
                        onChange={(value) => updateParam("mcc", value)}
                        locked={mccLocked}
                        icon={<TagIcon />}
                        searchPlaceholder={t("payfac.pageRatePlanParamsMccSearch")}
                        emptyText={t("payfac.pageRatePlanParamsMccEmpty")}
                    />
                )}
            </div>
        </section>
    );
};
